import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "maplibre-gl/dist/maplibre-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

export default function MapWidget() {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const drawRef = useRef(null);
  const [basemap, setBasemap] = useState("positron");
  const [polygonStyle, setPolygonStyle] = useState({
    color: "#B76E79",
    opacity: 0.8,
    strokeWidth: 2,
    strokeColor: "#000000"
  });
  const [isPolygonToolActive, setIsPolygonToolActive] = useState(false);

  const basemaps = {
    positron: "https://demotiles.maplibre.org/style.json",
    voyager: "https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json",
    dark: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
  };

  useEffect(() => {
    if (!mapContainer.current) return;

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: basemaps[basemap],
      center: [3.35, 49.85],
      zoom: 5,
    });

    map.on('load', () => {
      console.log('Map loaded successfully');
    });

    map.on('error', (e) => {
      console.error('Map error:', e);
    });

    mapRef.current = map;

    new maplibregl.Marker({ color: "red" })
      .setLngLat([18.06, 59.33])
      .setPopup(new maplibregl.Popup().setHTML("<b>Hello GISVEC!</b>"))
      .addTo(map);

    // DRAW TOOL with default styles
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {},
      defaultMode: "simple_select",
    });

    map.addControl(draw);
    drawRef.current = draw;

    // Listen for mode changes
    map.on('draw.modechange', (e) => {
      setIsPolygonToolActive(e.mode === 'draw_polygon');
    });

    return () => map.remove();
  }, [basemap]);

  // Update polygon styles dynamically on the map
  useEffect(() => {
    if (!mapRef.current || !drawRef.current) return;

    try {
      // Update existing polygon fill styles
      if (mapRef.current.getLayer('gl-draw-polygon-fill-inactive')) {
        mapRef.current.setPaintProperty('gl-draw-polygon-fill-inactive', 'fill-color', polygonStyle.color);
        mapRef.current.setPaintProperty('gl-draw-polygon-fill-inactive', 'fill-opacity', polygonStyle.opacity);
      }
      if (mapRef.current.getLayer('gl-draw-polygon-stroke-inactive')) {
        mapRef.current.setPaintProperty('gl-draw-polygon-stroke-inactive', 'line-color', polygonStyle.strokeColor);
        mapRef.current.setPaintProperty('gl-draw-polygon-stroke-inactive', 'line-width', polygonStyle.strokeWidth);
      }
      if (mapRef.current.getLayer('gl-draw-polygon-fill-active')) {
        mapRef.current.setPaintProperty('gl-draw-polygon-fill-active', 'fill-color', polygonStyle.color);
        mapRef.current.setPaintProperty('gl-draw-polygon-fill-active', 'fill-opacity', polygonStyle.opacity * 0.5);
      }
      if (mapRef.current.getLayer('gl-draw-polygon-stroke-active')) {
        mapRef.current.setPaintProperty('gl-draw-polygon-stroke-active', 'line-color', polygonStyle.strokeColor);
        mapRef.current.setPaintProperty('gl-draw-polygon-stroke-active', 'line-width', polygonStyle.strokeWidth);
      }
    } catch (error) {
      console.log('Layers might not exist yet - this is normal on first load');
    }
  }, [polygonStyle]); // Only update when polygonStyle changes

  // Update polygon style
  const updatePolygonStyle = (newStyle) => {
    setPolygonStyle(prev => ({ ...prev, ...newStyle }));
  };

  // --- Toolbar functions ---
  const drawPoint = () => {
    drawRef.current?.changeMode("draw_point");
    setIsPolygonToolActive(false);
  };
  const drawLine = () => {
    drawRef.current?.changeMode("draw_line_string");
    setIsPolygonToolActive(false);
  };
  const drawPolygon = () => {
    drawRef.current?.changeMode("draw_polygon");
    setIsPolygonToolActive(true);
  };
  const deleteSelected = () => drawRef.current?.trash();

  // Export to GeoJSON
  const exportGeoJSON = () => {
    const data = drawRef.current?.getAll();
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "drawings.geojson";
    a.click();
  };

  // Other tools
  const zoomIn = () => mapRef.current?.zoomTo(mapRef.current.getZoom() + 1);
  const zoomOut = () => mapRef.current?.zoomTo(mapRef.current.getZoom() - 1);
  const resetView = () => {
    mapRef.current?.flyTo({ center: [18.06, 59.33], zoom: 10 });
  };
  const toggleBasemap = () => {
    const keys = Object.keys(basemaps);
    const next = (keys.indexOf(basemap) + 1) % keys.length;
    setBasemap(keys[next]);
  };

  return (
    <div
      style={{
        position: "absolute",
        top: "60px",
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
      }}
    >
      <div
        ref={mapContainer}
        style={{
          width: "100%",
          height: "100%",
        }}
      />

      {/* POLYGON STYLING PANEL - ONLY SHOWS WHEN POLYGON TOOL IS ACTIVE */}
      {isPolygonToolActive && (
        <div
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            background: "rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            padding: "15px",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            border: "2px solid rgba(0, 0, 0, 0.3)",
            maxWidth: "250px",
            zIndex: 1001,
          }}
        >
          <h4 style={{ margin: "0 0 10px 0", color: "#000", fontSize: "14px" }}>Polygon Style</h4>
          
          <div style={{ marginBottom: "10px" }}>
            <label style={{ display: "block", fontSize: "12px", marginBottom: "4px" }}>Fill Color</label>
            <input
              type="color"
              value={polygonStyle.color}
              onChange={(e) => updatePolygonStyle({ color: e.target.value })}
              style={{ width: "100%", height: "30px", border: "none", borderRadius: "4px" }}
            />
          </div>

          <div style={{ marginBottom: "10px" }}>
            <label style={{ display: "block", fontSize: "12px", marginBottom: "4px" }}>
              Opacity: {Math.round(polygonStyle.opacity * 100)}%
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={polygonStyle.opacity}
              onChange={(e) => updatePolygonStyle({ opacity: parseFloat(e.target.value) })}
              style={{ width: "100%" }}
            />
          </div>

          <div style={{ marginBottom: "10px" }}>
            <label style={{ display: "block", fontSize: "12px", marginBottom: "4px" }}>Stroke Width</label>
            <input
              type="range"
              min="1"
              max="10"
              step="1"
              value={polygonStyle.strokeWidth}
              onChange={(e) => updatePolygonStyle({ strokeWidth: parseInt(e.target.value) })}
              style={{ width: "100%" }}
            />
          </div>

          <div style={{ marginBottom: "10px" }}>
            <label style={{ display: "block", fontSize: "12px", marginBottom: "4px" }}>Stroke Color</label>
            <input
              type="color"
              value={polygonStyle.strokeColor}
              onChange={(e) => updatePolygonStyle({ strokeColor: e.target.value })}
              style={{ width: "100%", height: "30px", border: "none", borderRadius: "4px" }}
            />
          </div>
        </div>
      )}

      {/* TOOLBAR */}
      <div
        style={{
          position: "absolute",
          bottom: "15px",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: "10px",
          background: "rgba(255, 255, 255, 0.2)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          padding: "10px 14px",
          borderRadius: "25px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          border: "2px solid rgba(0, 0, 0, 0.3)",
        }}
      >
        {/* Base tools */}
        <button className="gisvec-btn" onClick={zoomIn}>＋</button>
        <button className="gisvec-btn" onClick={zoomOut}>－</button>
        <button className="gisvec-btn" onClick={toggleBasemap}>🗺️</button>
        <button className="gisvec-btn" onClick={resetView}>⟳</button>

        {/* Draw tools */}
        <button className="gisvec-btn" onClick={drawPoint}>⚪</button>
        <button className="gisvec-btn" onClick={drawLine}>➖</button>
        <button className="gisvec-btn" onClick={drawPolygon}>⬛</button>
        <button className="gisvec-btn" onClick={deleteSelected}>🗑️</button>

        {/* Export */}
        <button className="gisvec-btn" onClick={exportGeoJSON}>💾</button>
      </div>

      <style>
        {`
          .gisvec-btn {
            background: rgba(255, 255, 255, 0.3);
            border: 2px solid rgba(0, 0, 0, 0.3);
            padding: 6px 10px;
            font-size: 18px;
            border-radius: 6px;
            cursor: pointer;
            transition: 0.2s;
            backdropFilter: blur(10px);
            color: #000000;
          }
          .gisvec-btn:hover {
            background: rgba(255, 255, 255, 0.5);
          }
        `}
      </style>
    </div>
  );
}